import { Check } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Stepper used at the top of every multi-step wizard.
 * Steps are 1-indexed visually; pass current as the *zero-indexed* step.
 */
export function Stepper({
  steps,
  current,
  className,
}: {
  steps: string[];
  current: number; // 0-indexed
  className?: string;
}) {
  return (
    <ol
      className={cn('flex items-center gap-2 sm:gap-3 flex-wrap', className)}
      aria-label="Wizard progress"
    >
      {steps.map((label, idx) => {
        const state = idx < current ? 'done' : idx === current ? 'current' : 'upcoming';
        return (
          <li key={label} className="flex items-center gap-2 sm:gap-3 text-sm">
            <span
              className={cn(
                'w-6 h-6 grid place-items-center rounded-full text-xs font-semibold tabular-nums shrink-0 transition',
                state === 'done' && 'bg-emerald-600 text-white',
                state === 'current' && 'bg-brand text-white dark:bg-brand-dm dark:text-zinc-950',
                state === 'upcoming' && 'bg-zinc-200 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400',
              )}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              {state === 'done' ? <Check className="w-3.5 h-3.5" /> : idx + 1}
            </span>
            <span
              className={cn(
                'font-medium hidden sm:inline',
                state === 'current'
                  ? 'text-zinc-950 dark:text-zinc-50'
                  : state === 'done'
                  ? 'text-zinc-600 dark:text-zinc-300'
                  : 'text-zinc-400 dark:text-zinc-500',
              )}
            >
              {label}
            </span>
            {idx < steps.length - 1 && (
              <span
                className={cn(
                  'w-6 sm:w-12 h-px',
                  idx < current ? 'bg-emerald-300 dark:bg-emerald-800' : 'bg-zinc-200 dark:bg-zinc-700',
                )}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
